import type { DateRange } from "@repo/app-types/entities/dateRange";
import {
  findNodeWithPath,
  isPlaybookSectionNode,
  type PlaybookId,
  type PlaybookNode,
  type PlaybookNodeId,
} from "@repo/app-types/entities/playbook";
import { renderPlural } from "@repo/app-types/helpers/utilities";
import {
  MultiSelect,
  type MultiSelectOption,
} from "@repo/ui/Select/multiSelect";
import { useNow } from "@repo/ui/timestamps/timestampContext";
import { Duration } from "luxon";
import {
  useEffect,
  useMemo,
  useRef,
  useState,
  type FC,
  type ReactNode,
} from "react";
import { useReportDataStreamQuery } from "../../api/entities/performanceReport.js";
import { usePlaybookByIdQuery } from "../../api/entities/playbook.js";
import {
  buildTeamPerformancePageFilters,
  dateRangeToISORange,
} from "../filters/buildFilterQueryArgs.js";
import { DateRangeFilterItem } from "../filters/dateRangeFilterItem.js";
import { useFilters } from "../filters/filters.js";
import { usePlaybookViewDefaultDateRange } from "../filters/usePlaybookFilterDefaults.js";
import type { ReportRenderProps } from "./baseReport.js";

const FALLBACK_DEFAULT_PERIOD: DateRange = {
  length: Duration.fromObject({ days: 7 }),
  end: undefined,
};

export interface PerformanceReportProps {
  playbookId: PlaybookId;
  children: (props: ReportRenderProps) => ReactNode;
}

export const PerformanceReport: FC<PerformanceReportProps> = (props) => {
  const { playbookId } = props;

  const now = useNow();
  const endOfYesterday = useMemo(
    () => now.minus({ days: 1 }).endOf("day"),
    [now],
  );
  const [
    {
      teammateGroupIds,
      outcome,
      customFields,
      playbookConditions,
      totalRecordingDuration,
      recordingCount,
    },
    renderFilters,
  ] = useFilters(
    [
      "teammateGroupIds",
      "outcome",
      "customFields",
      "playbookConditions",
      "totalRecordingDuration",
      "recordingCount",
    ],
    { playbookId },
  );

  const defaultPeriod = usePlaybookViewDefaultDateRange(playbookId, "team");
  const [customPeriod, setPeriod] = useState<DateRange | undefined>(undefined);
  const period: DateRange =
    customPeriod ?? defaultPeriod ?? FALLBACK_DEFAULT_PERIOD;

  const [playbookNodeIds, setPlaybookNodeIds] = useState<
    readonly PlaybookNodeId[] | undefined
  >(undefined);

  const prevPlaybookIdRef = useRef(playbookId);
  useEffect(() => {
    if (playbookId !== prevPlaybookIdRef.current) {
      setPlaybookNodeIds(undefined);
    }

    prevPlaybookIdRef.current = playbookId;
  }, [playbookId]);

  const { currentData: playbook } = usePlaybookByIdQuery(playbookId);
  const defaultPlaybookNodeIds = useMemo<
    readonly PlaybookNodeId[] | undefined
  >(() => {
    if (!playbook?.assessment_tree) {
      return undefined;
    }

    return playbook.assessment_tree.nodes
      .filter(isPlaybookSectionNode)
      .map((node) => node.id);
  }, [playbook?.assessment_tree]);
  const selectedPlaybookNodeIds = playbookNodeIds ?? defaultPlaybookNodeIds;

  const playbookNodeOptions = useMemo(() => {
    if (!playbook?.assessment_tree) {
      return [];
    }

    function* traverse(
      nodes: readonly PlaybookNode[],
      depth: number,
    ): Iterable<MultiSelectOption> {
      for (const node of nodes) {
        if (isPlaybookSectionNode(node)) {
          yield { id: node.id, label: node.name, indent: depth };
          yield* traverse(node.nodes, depth + 1);
        } else {
          yield { id: node.id, label: node.name, indent: depth };
        }
      }
    }

    return [
      {
        id: "nodes",
        options: [...traverse(playbook.assessment_tree.nodes, 0)],
      },
    ];
  }, [playbook?.assessment_tree]);

  const playbookNodesLabel = useMemo(() => {
    if (!playbook?.assessment_tree || !selectedPlaybookNodeIds?.length) {
      return undefined;
    }

    const [firstNodeId] = selectedPlaybookNodeIds;
    if (selectedPlaybookNodeIds.length === 1 && firstNodeId !== undefined) {
      const node = findNodeWithPath(
        playbook.assessment_tree,
        firstNodeId,
      )?.node;
      return node?.name ?? "Unknown criterion";
    }

    return renderPlural(selectedPlaybookNodeIds.length, {
      one: "1 criterion",
      other: (count) => `${count.toLocaleString()} criteria`,
    });
  }, [playbook?.assessment_tree, selectedPlaybookNodeIds]);

  const { data, isFetching, refetch } = useReportDataStreamQuery({
    type: "performance",
    periodLengthDays: Math.round(period.length.as("days")),
    periodEnd: period.end ?? endOfYesterday,
    timeZone: now.zone,
    playbookId,
    playbookNodeIds: selectedPlaybookNodeIds,
    filters: buildTeamPerformancePageFilters({
      playbookId,
      teammateGroupIds: teammateGroupIds ?? null,
      outcome,
      customFields,
      playbookConditions,
      totalRecordingDuration,
      recordingCount,
      signalId: null,
      signalMetadataFields: [],
      selectedDateRange: dateRangeToISORange(now, {
        length: period.length,
        end: period.end ?? endOfYesterday,
      }),
      selectedDateRangeTimeZone: now.zoneName,
    }),
  });

  return props.children({
    title: "Performance report",
    isScopedToPlaybook: true,
    settings: (
      <MultiSelect
        label={playbookNodesLabel}
        placeholder="Select criteria to show"
        sections={playbookNodeOptions}
        value={selectedPlaybookNodeIds ?? []}
        onChange={setPlaybookNodeIds}
      />
    ),
    renderFilterBar: (options) =>
      renderFilters({
        isLoading: isFetching,
        trailingItems: (
          <DateRangeFilterItem
            hasBasisPicker={false}
            value={period}
            onChange={setPeriod}
          />
        ),
        rightActions: options?.rightActions,
      }),
    onRefresh: refetch,
    state: data?.state,
  });
};
